import { supabase } from '../supabaseClient';

// Local storage key for fallback usage tracking
const USAGE_TRACKING_KEY = 'irecruiter_usage_tracking';

/**
 * Usage tracking service for monitoring subscription usage
 */
export interface UsageResponse {
  success: boolean;
  error?: string;
  newUsed?: number;
  remainingUses?: number;
  subscription?: any;
  timestamp?: string;
}

/**
 * Make a fetch request with retry logic
 */
async function fetchWithRetry(url: string, options: RequestInit, retries = 3, backoff = 300): Promise<Response> {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    if (retries <= 1) throw error;
    
    await new Promise(resolve => setTimeout(resolve, backoff));
    return fetchWithRetry(url, options, retries - 1, backoff * 2);
  }
}

/**
 * Increment usage count for the current user
 * @param count Number of analyses to increment by (default: 1)
 */
export const incrementUsage = async (count: number = 1): Promise<UsageResponse> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    
    if (!user) {
      console.error('No user logged in');
      return { success: false, error: 'No user logged in' };
    }

    // Call the increment-usage API with retry logic
    const response = await fetchWithRetry('/api/increment-usage', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.id,
        count,
      }),
    }, 3, 500);

    if (!response.ok) {
      // If we get a 402 response, it means the usage limit was exceeded
      if (response.status === 402) {
        const data = await response.json();
        console.warn('Usage limit exceeded:', data);
        
        // Return detailed information about the limit
        return {
          success: false,
          error: 'Usage limit exceeded',
          newUsed: data.currentUsed,
          remainingUses: 0,
          subscription: data.subscription
        };
      }
      
      // For other errors, try to directly update the subscription in Supabase
      console.error(`API call failed with status ${response.status}. Trying direct update...`);
      
      try {
        // Import the direct increment function
        const { incrementAnalysesUsed } = await import('./subscriptionService');
        
        // Try to increment the subscription directly
        const directSuccess = await incrementAnalysesUsed(user.id, count);
        
        if (directSuccess) {
          console.info('Successfully incremented usage count through direct Supabase update');
          
          // Get the updated subscription to return
          const { checkUserCanAnalyze } = await import('./subscriptionService');
          const subscriptionStatus = await checkUserCanAnalyze(user.id);
          
          return {
            success: true,
            error: undefined,
            newUsed: subscriptionStatus.remainingAnalyses,
            remainingUses: subscriptionStatus.remainingAnalyses,
            subscription: {
              analyses_used: subscriptionStatus.remainingAnalyses,
              plan_type: subscriptionStatus.currentPlan
            }
          };
        } else {
          console.error('Direct Supabase update also failed');
        }
      } catch (directUpdateError) {
        console.error('Error during direct subscription update:', directUpdateError);
      }
      
      // For other errors
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP error ${response.status}: ${response.statusText}` };
      }
      
      console.error('Error incrementing usage:', errorData);
      return {
        success: false,
        error: errorData.error || `Failed to increment usage (${response.status})`
      };
    }

    // Successfully incremented
    let data;
    try {
      data = await response.json();
    } catch (e) {
      console.error('Error parsing response:', e);
      return {
        success: false,
        error: 'Failed to parse server response'
      };
    }
    
    return data;
  } catch (error) {
    console.error('Error in incrementUsage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Get the current subscription details including usage
 */
export const getSubscriptionUsage = async (): Promise<any> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    
    if (!user) {
      console.error('No user logged in');
      return null;
    }

    // Use the get-subscription API endpoint with retry
    const response = await fetchWithRetry(
      `/api/get-subscription?userId=${user.id}`, 
      { method: 'GET' },
      3,
      500
    );
    
    if (!response.ok) {
      console.error('Error fetching subscription usage:', response.statusText);
      return null;
    }

    const data = await response.json();
    
    // Validate the response data
    if (!data || (data.error && !data.subscription)) {
      console.error('Invalid subscription data:', data);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getSubscriptionUsage:', error);
    return null;
  }
};

/**
 * Check if the user can perform another analysis
 */
export const canPerformAnalysis = async (): Promise<{
  canPerform: boolean;
  remainingUses?: number;
  subscription?: any;
  message?: string;
}> => {
  try {
    const subscription = await getSubscriptionUsage();
    
    if (!subscription) {
      return { 
        canPerform: false, 
        message: 'Failed to retrieve subscription information'
      };
    }
    
    if (!subscription.subscription || !subscription.subscription.is_active) {
      return { 
        canPerform: false, 
        subscription: subscription.subscription,
        message: 'No active subscription'
      };
    }
    
    const usageLimit = subscription.subscription.analyses_limit || 
      (subscription.subscription.plan_type === 'premium' ? 1500 : 
       subscription.subscription.plan_type === 'standard' ? 500 : 10);
       
    const usedCount = subscription.subscription.analyses_used || 0;
    const remaining = Math.max(0, usageLimit - usedCount);
    
    return {
      canPerform: remaining > 0,
      remainingUses: remaining,
      subscription: subscription.subscription,
      message: remaining > 0 ? 
        `You have ${remaining} analyses remaining` : 
        'You have reached your usage limit'
    };
  } catch (error) {
    console.error('Error in canPerformAnalysis:', error);
    return {
      canPerform: false,
      message: 'Failed to check analysis permission'
    };
  }
};

// Add helper functions for local storage fallback
/**
 * Save usage tracking record to local storage as fallback
 */
function saveUsageToLocalStorage(userId: string, analysisType: string): void {
  try {
    const timestamp = new Date().toISOString();
    const key = `${USAGE_TRACKING_KEY}_${userId}`;
    
    // Get existing records or initialize empty array
    const existingData = localStorage.getItem(key);
    const records = existingData ? JSON.parse(existingData) : [];
    
    // Add new record
    records.push({
      user_id: userId,
      analysis_type: analysisType,
      timestamp,
      synced: false
    });
    
    // Save back to local storage
    localStorage.setItem(key, JSON.stringify(records));
    console.info('Saved usage tracking to local storage as fallback');
  } catch (error) {
    console.error('Failed to save usage tracking to local storage:', error);
  }
}

/**
 * Usage monitoring hook that can be used to track usage across the application
 */
export const trackUsage = async (analysisType: string, count: number = 1): Promise<boolean> => {
  try {
    // Check if user can perform analysis
    const { canPerform, message } = await canPerformAnalysis();
    
    if (!canPerform) {
      console.warn(`Cannot perform ${analysisType} analysis:`, message);
      return false;
    }
    
    // If they can perform, increment the usage by the specified count
    const result = await incrementUsage(count);
    
    if (!result.success) {
      console.error(`Failed to track usage for ${analysisType}:`, result.error);
      return false;
    }
    
    // Log the usage for analytics purposes
    console.info(`Tracked usage for ${analysisType}. Remaining: ${result.remainingUses}`);
    
    // Also track in Supabase for audit purposes
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    
    if (user) {
      try {
        // Add retry logic for the Supabase operation
        let success = false;
        let lastError: any = null;
        
        // Try up to 3 times with exponential backoff
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            // Changed from 'analysis_logs' to 'analyzed_resumes'
            const { error } = await supabase.from('analyzed_resumes').insert({
          user_id: user.id,
          analyzed_at: new Date().toISOString()
        });
        
        if (error) {
              console.error(`Attempt ${attempt}: Failed to log analysis in Supabase:`, error);
              lastError = error;
              
              // If it's not the last attempt, wait before retrying
              if (attempt < 3) {
                const delay = Math.pow(2, attempt) * 300; // Exponential backoff: 600ms, 1200ms
                console.info(`Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            } else {
              success = true;
              console.info('Successfully logged analysis in Supabase');
              break; // Exit the retry loop
            }
          } catch (attemptError) {
            console.error(`Attempt ${attempt}: Exception during Supabase insert:`, attemptError);
            lastError = attemptError;
            
            // If it's not the last attempt, wait before retrying
            if (attempt < 3) {
              const delay = Math.pow(2, attempt) * 300;
              console.info(`Retrying in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        // If all attempts failed, use local storage fallback
        if (!success) {
          console.error('All attempts to log analysis in Supabase failed');
          
          // Provide more diagnostic information about the error
          if (lastError) {
            if (lastError.code) {
              console.error('Error code:', lastError.code);
            }
            if (lastError.details) {
              console.error('Error details:', lastError.details);
            }
            if (lastError.hint) {
              console.error('Error hint:', lastError.hint);
            }
          }
          
          // Save to local storage as fallback
          saveUsageToLocalStorage(user.id, analysisType);
        }
      } catch (logError) {
        console.error('Exception handling analysis logging:', logError);
        
        // Save to local storage as fallback
        if (user?.id) {
          saveUsageToLocalStorage(user.id, analysisType);
        }
      }
    }
    
    // Notify all components that usage has been updated
    try {
      window.dispatchEvent(new Event('usage-updated'));
      console.info('Notified components of usage update');
    } catch (eventError) {
      console.warn('Failed to dispatch usage update event:', eventError);
    }
    
    return true;
  } catch (error) {
    console.error('Error tracking usage:', error);
    
    // Try to save to local storage as last resort
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (user?.id) {
        saveUsageToLocalStorage(user.id, analysisType);
      }
    } catch (e) {
      console.error('Failed to get user for fallback storage:', e);
    }
    
    return false;
  }
};

/**
 * Sync local storage usage data with Supabase
 * Call this function when the app initializes or when the network is restored
 */
export const syncLocalUsageData = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    
    if (!user) {
      console.warn('No user logged in, cannot sync usage data');
      return false;
    }
    
    const key = `${USAGE_TRACKING_KEY}_${user.id}`;
    const localData = localStorage.getItem(key);
    
    if (!localData) {
      // No local data to sync
      return true;
    }
    
    const records = JSON.parse(localData);
    if (!Array.isArray(records) || records.length === 0) {
      // No records to sync or invalid data
      localStorage.removeItem(key);
      return true;
    }
    
    console.info(`Found ${records.length} local usage records to sync`);
    const successfulSyncs: Array<{
      user_id: string;
      analysis_type: string;
      timestamp: string;
      synced: boolean;
    }> = [];
    
    // Process each record
    for (const record of records) {
      if (record.synced) continue;
      
      try {
        // Try to sync with Supabase
        const { error } = await supabase.from('analyzed_resumes').insert({
          user_id: user.id,
          analyzed_at: record.timestamp
        });
        
        if (error) {
          console.error('Failed to sync usage record:', error);
        } else {
          successfulSyncs.push(record);
        }
      } catch (syncError) {
        console.error('Error syncing usage record:', syncError);
      }
    }
    
    // Update local storage with synced status
    if (successfulSyncs.length > 0) {
      const updatedRecords = records.map(record => {
        const wasSuccessful = successfulSyncs.some(
          syncedRecord => syncedRecord.timestamp === record.timestamp
        );
        return wasSuccessful ? { ...record, synced: true } : record;
      });
      
      // Save the updated records
      localStorage.setItem(key, JSON.stringify(updatedRecords));
      
      // If all records have been synced, we can remove them
      const allSynced = updatedRecords.every(record => record.synced);
      if (allSynced) {
        localStorage.removeItem(key);
      }
      
      console.info(`Successfully synced ${successfulSyncs.length} of ${records.length} usage records`);
    }
    
    return successfulSyncs.length > 0;
  } catch (error) {
    console.error('Error syncing local usage data:', error);
    return false;
  }
}; 